import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { evaluateMergeRules } from '@gitbook/app-merge-rules';

import { apiQuery, useLoggedInUser } from '../../state';
import { useSpaceMergeRules } from './useSpaceMergeRules';

/**
 * Evaluate the merge rule for a given change request.
 */
export function useEvaluateMergeRules(input: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
}) {
    const { space, changeRequest } = input;

    // Get the merge rule to use
    const currentUser = useLoggedInUser();
    const mergeRules = useSpaceMergeRules(space);

    // Get the reviews for the change request
    // Only if we'll need them
    const { data: reviewsData } = useSuspenseQuery(
        apiQuery(
            'getReviewsByChangeRequestId',
            mergeRules.type !== 'none'
                ? {
                      spaceId: space.id,
                      changeRequestId: changeRequest.id,
                      limit: 100,
                      outdated: false,
                  }
                : skipToken
        )
    );

    const evaluationContext = React.useMemo<api.MergeRulesEvaluationContext>(() => {
        return {
            space,
            changeRequest,
            reviews: reviewsData?.items ?? [],

            // To avoid doing an extra API call just to fetch the current user, we use the firebase user data
            actor: {
                object: 'user',
                id: currentUser.uid,
                displayName: currentUser.displayName ?? '',
                email: currentUser.email ?? undefined,
                photoURL: currentUser.photoURL ?? undefined,
                urls: {
                    location: '',
                },
            },
        };
    }, [space, changeRequest, reviewsData, currentUser]);

    return React.useMemo(() => {
        return evaluateMergeRules({
            mergeRules,
            context: evaluationContext,
        });
    }, [mergeRules, evaluationContext]);
}
